const TRUSTED_EMBEDDED_HOST_ORIGINS = (() => {
  const configured = process.env.REACT_APP_TRUSTED_HOST_ORIGINS ?? '';
  const origins = new Set<string>();

  for (const entry of configured.split(',')) {
    const candidate = entry.trim();
    if (!candidate) continue;

    try {
      origins.add(new URL(candidate).origin);
    } catch {
      // Ignore invalid entries and continue processing configured origins.
    }
  }

  return origins;
})();

export type HostMessageTrustReason = 'trusted' | 'source_mismatch' | 'origin_mismatch';

export interface HostMessageTrustDecision {
  trusted: boolean;
  reason: HostMessageTrustReason;
  expectedOrigin: string | null;
  allowedEmbeddedOrigins: string[];
}

export const evaluateTrustedHostMessageEvent = (event: MessageEvent<unknown>): HostMessageTrustDecision => {
  if (window.parent !== window && event.source !== window.parent) {
    return {
      trusted: false,
      reason: 'source_mismatch',
      expectedOrigin: null,
      allowedEmbeddedOrigins: Array.from(TRUSTED_EMBEDDED_HOST_ORIGINS),
    };
  }

  if (window.parent === window) {
    const expectedOrigin = window.location.origin;
    const trusted = event.origin === expectedOrigin;

    return {
      trusted,
      reason: trusted ? 'trusted' : 'origin_mismatch',
      expectedOrigin,
      allowedEmbeddedOrigins: [],
    };
  }

  const trusted = TRUSTED_EMBEDDED_HOST_ORIGINS.has(event.origin);

  return {
    trusted,
    reason: trusted ? 'trusted' : 'origin_mismatch',
    expectedOrigin: null,
    allowedEmbeddedOrigins: Array.from(TRUSTED_EMBEDDED_HOST_ORIGINS),
  };
};

export const isTrustedHostMessageEvent = (event: MessageEvent<unknown>): boolean => {
  return evaluateTrustedHostMessageEvent(event).trusted;
};
